import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { DragStart, DragUpdate } from 'react-beautiful-dnd';

import { type Point, getAvatars, getDraggedCenter, getDropArea } from './talentReviewDropDom';

/**
 * Where the person being dragged would land if released now.
 *
 * react-beautiful-dnd only knows single-axis lists, and a cell wraps its
 * avatars, so the index it reports is off as soon as a cell has more than one
 * row. The page tracks the index itself: the dragged avatar's centre against
 * the rows of avatars in the cell under it. The cell opens a gap there, and
 * the drop animation aims at that gap.
 */
export type TalentReviewInsertion = {
	/** The person being dragged. */
	draggableId: string;
	/** The cell under the dragged avatar, and the index among the others in it. */
	target?: { droppableId: string; index: number };
};

export const TalentReviewInsertionContext = createContext<TalentReviewInsertion | undefined>(
	undefined,
);

export const useTalentReviewInsertion = () => useContext(TalentReviewInsertionContext);

/**
 * The index a dragged avatar at `point` takes among the avatars of a drop area
 * that wraps its rows: the row the point is on (the first row when above them
 * all, the last when below), then the number of avatars on that row whose
 * centre is left of the point.
 * @param droppableId the droppable under the point
 * @param draggableId the dragged person, skipped when it is rendered in this droppable
 * @param point the centre of the dragged avatar
 * @returns the index among the other avatars; 0 when the area is empty or missing
 */
export const getInsertionIndex = (droppableId: string, draggableId: string, point: Point) => {
	const area = getDropArea(droppableId);
	if (!area) {
		return 0;
	}

	const rects = getAvatars(area)
		.filter((element) => element.dataset.rbdDraggableId !== draggableId)
		.map((element) => element.getBoundingClientRect());
	if (rects.length === 0) {
		return 0;
	}

	const rows: { top: number; bottom: number; start: number; end: number }[] = [];
	rects.forEach((rect, index) => {
		const row = rows[rows.length - 1];
		if (row && Math.abs(rect.top - row.top) < rect.height / 2) {
			row.end = index + 1;
			row.bottom = Math.max(row.bottom, rect.bottom);
		} else {
			rows.push({ top: rect.top, bottom: rect.bottom, start: index, end: index + 1 });
		}
	});

	const row = rows.find((candidate) => point.y < candidate.bottom) ?? rows[rows.length - 1];
	let index = row.start;
	for (let i = row.start; i < row.end; i += 1) {
		const rect = rects[i];
		if (point.x > rect.left + rect.width / 2) {
			index = i + 1;
		}
	}
	return index;
};

/**
 * Tracks the insertion point during a drag. Wire the returned handlers into the
 * `DragDropContext` and provide `insertion` through `TalentReviewInsertionContext`.
 *
 * dnd reports the droppable under the dragged avatar (`onDragUpdate`); the
 * index within it follows the avatar's centre, recomputed on every pointer
 * move and again a frame later (dnd positions the avatar after the event).
 * Only a change of cell or index causes a render.
 * @returns the current insertion, a getter for use in `onDragEnd`, and the dnd handlers
 */
export function useTalentReviewInsertionTracking() {
	const [insertion, setInsertion] = useState<TalentReviewInsertion>();
	const insertionRef = useRef<TalentReviewInsertion>();
	const drag = useRef<{ draggableId: string; droppableId?: string; pointer?: Point }>();

	const update = useCallback(() => {
		const current = drag.current;
		if (!current) {
			return;
		}

		const { draggableId, droppableId } = current;
		let target: TalentReviewInsertion['target'];
		if (droppableId?.startsWith('cell-')) {
			// The dragged avatar's centre, which is also what dnd picks the droppable
			// by, so the slot and the drop agree with the cell dnd reports. The
			// pointer can sit well outside that cell (a lifted avatar is grabbed
			// away from its centre; a wide card even more so), and an index taken
			// from it would land the person at the cell's edge. dnd positions the
			// avatar a frame after the pointer moves; the rAF re-check below
			// catches up.
			const point = getDraggedCenter(draggableId) ?? current.pointer;
			if (point) {
				target = { droppableId, index: getInsertionIndex(droppableId, draggableId, point) };
			}
		}

		const previous = insertionRef.current;
		if (
			previous?.draggableId === draggableId &&
			previous.target?.droppableId === target?.droppableId &&
			previous.target?.index === target?.index
		) {
			return;
		}
		const next = { draggableId, target };
		insertionRef.current = next;
		setInsertion(next);
	}, []);

	const dragging = insertion !== undefined;
	useEffect(() => {
		if (!dragging) {
			return undefined;
		}

		// dnd moves the avatar in its own frame after the pointer event, so the
		// index is taken again on the next frame.
		let frame: number | undefined;
		const scheduleRecheck = () => {
			if (frame === undefined) {
				frame = requestAnimationFrame(() => {
					frame = undefined;
					update();
				});
			}
		};
		const onMouseMove = (event: MouseEvent) => {
			if (drag.current) {
				drag.current.pointer = { x: event.clientX, y: event.clientY };
				update();
				scheduleRecheck();
			}
		};
		const onTouchMove = (event: TouchEvent) => {
			const touch = event.touches[0];
			if (drag.current && touch) {
				drag.current.pointer = { x: touch.clientX, y: touch.clientY };
				update();
				scheduleRecheck();
			}
		};
		window.addEventListener('mousemove', onMouseMove, { passive: true });
		window.addEventListener('touchmove', onTouchMove, { passive: true });
		return () => {
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('touchmove', onTouchMove);
			if (frame !== undefined) {
				cancelAnimationFrame(frame);
			}
		};
	}, [dragging, update]);

	const onDragStart = useCallback(
		(start: DragStart) => {
			drag.current = { draggableId: start.draggableId, droppableId: start.source.droppableId };
			update();
		},
		[update],
	);

	const onDragUpdate = useCallback(
		(event: DragUpdate) => {
			if (drag.current) {
				drag.current.droppableId = event.destination?.droppableId;
				update();
			}
		},
		[update],
	);

	const onDragEnd = useCallback(() => {
		drag.current = undefined;
		insertionRef.current = undefined;
		setInsertion(undefined);
	}, []);

	/** The insertion as last tracked; for `onDragEnd`, which runs before the state clears. */
	const getInsertion = useCallback(() => insertionRef.current, []);

	return { insertion, getInsertion, onDragStart, onDragUpdate, onDragEnd };
}
